import { useEffect, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { ChoiceControl, DefinitionList, FeedbackSnackbar, FormDialog, FormTextField, InlineNotice, ResourceState } from '../../design-system';
import { useAsyncResource } from '../../shared/useAsyncResource';
import { absenceSyncScheduleError, defaultAbsenceSyncSchedule } from './absenceSyncSchedule';
import type { AbsenceSyncSchedule } from './model';
import type { AbsencePort } from './port';

export function AbsenceSyncScheduleDialog({ open, onClose, port, workerId, fixtureMode, canWrite }: {
  open: boolean; onClose: () => void; port: AbsencePort; workerId: string; fixtureMode: boolean; canWrite: boolean;
}) {
  const resource = useAsyncResource(() => !open ? Promise.resolve(undefined) : fixtureMode ? Promise.resolve(defaultAbsenceSyncSchedule) : port.getSyncSchedule(workerId), [open, port, workerId, fixtureMode]);
  const [saved, setSaved] = useState<AbsenceSyncSchedule>();
  const [draft, setDraft] = useState<AbsenceSyncSchedule>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [conflict, setConflict] = useState(false);
  const [notice, setNotice] = useState(false);
  useEffect(() => {
    if (!open) { setSaved(undefined); setDraft(undefined); return; }
    if (resource.data) { setSaved(resource.data); setDraft(resource.data); setError(undefined); setConflict(false); }
  }, [open, resource.data]);
  const validation = draft ? absenceSyncScheduleError(draft) : undefined;
  const dirty = JSON.stringify(saved) !== JSON.stringify(draft);
  const change = (next: Partial<AbsenceSyncSchedule>) => setDraft((value) => value ? { ...value, ...next } : value);
  const save = async () => {
    if (!canWrite || !draft || !dirty || validation || conflict || saving || resource.loading || resource.error) return;
    setSaving(true); setError(undefined);
    try {
      const updated = fixtureMode
        ? { ...draft, ...(draft.useCompanyDefault ? defaultAbsenceSyncSchedule : {}), version: draft.version + 1 }
        : await port.updateSyncSchedule(workerId, draft);
      setSaved(updated); setDraft(updated); setNotice(true); onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : '휴가 표시 시간을 저장하지 못했습니다.');
      setConflict(typeof failure === 'object' && failure !== null && 'code' in failure && failure.code === 'VERSION_CONFLICT');
    } finally { setSaving(false); }
  };
  return <>
    <FormDialog open={open} onClose={onClose} title="휴가 표시 시간" help="Google 캘린더·Slack에 표시할 휴가 시간입니다. 실제 근무 기준과 연결 상태는 변경하지 않습니다." onSubmit={() => { void save(); }}
      submitLabel="저장" cancelLabel="닫기" busy={saving}
      submitDisabled={!canWrite || !dirty || Boolean(validation) || conflict || resource.loading || Boolean(resource.error)}>
      <ResourceState embedded density="compact" loading={resource.loading} empty={!draft} error={resource.error?.message} onRetry={resource.reload} loadingLabel="휴가 표시 시간을 불러오는 중입니다.">
        {draft && <Stack spacing={2}>
          {!canWrite && <InlineNotice severity="info">현재는 휴가 표시 시간을 조회만 할 수 있습니다.</InlineNotice>}
          {error && <InlineNotice severity="error" announcement="polite" action={conflict ? <Button onClick={resource.reload}>최신 설정 불러오기</Button> : undefined}>{error}{conflict ? ' 최신 설정을 불러오면 편집 내용이 초기화됩니다.' : ''}</InlineNotice>}
          {canWrite ? <>
            <ChoiceControl kind="switch" label="회사 기본값 사용" checked={draft.useCompanyDefault} disabled={saving} onCheckedChange={(useCompanyDefault) => change({ useCompanyDefault })} />
            {draft.useCompanyDefault ? <Typography variant="body2" color="text.secondary">저장 후 최신 회사 기본값이 적용됩니다.</Typography> : <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormTextField fullWidth type="time" required label="근무 시작" value={draft.workdayStart} disabled={saving} onChange={(event) => change({ workdayStart: event.target.value })} />
              <FormTextField fullWidth type="time" required label="근무 종료" value={draft.workdayEnd} disabled={saving} onChange={(event) => change({ workdayEnd: event.target.value })} />
            </Stack>}
            {draft.useCompanyDefault && saved?.useCompanyDefault && <DefinitionList items={[
              ['근무 시작', saved.workdayStart], ['근무 종료', saved.workdayEnd],
            ]} />}
          </> : <DefinitionList items={[
            ['시간 기준', draft.useCompanyDefault ? '회사 기본값' : '개인 설정'], ['근무 시작', draft.workdayStart], ['근무 종료', draft.workdayEnd],
          ]} />}
          <DefinitionList items={[
            ['시간대', draft.timeZone], ['반차 경계', '오후 2시 30분'],
            ['오전 반차', '근무 시작부터 오후 2시 30분까지'], ['오후 반차', '오후 2시 30분부터 근무 종료까지'],
            ['오전 반반차', '근무 시작부터 11:30까지 · 0.25일'], ['오후 반반차', '16:30부터 근무 종료까지 · 0.25일'],
          ]} />
          {validation && <InlineNotice severity="info">{validation}</InlineNotice>}
        </Stack>}
      </ResourceState>
    </FormDialog>
    <FeedbackSnackbar open={notice} tone="success" message="휴가 표시 시간을 저장했습니다." onClose={() => setNotice(false)} />
  </>;
}
